/** A camera target in continuous, normalized Web Mercator coordinates. */
export interface CameraTarget {
  readonly x: number;
  readonly y: number;
}

const HALF_WINDOW_SECONDS = 1.2;
const SAMPLE_STEPS = 16;
export const CAMERA_FOLLOW_KERNEL = Array.from({ length: SAMPLE_STEPS * 2 + 1 }, (_, index) => {
  const offset = (index - SAMPLE_STEPS) / SAMPLE_STEPS;
  return { offset, weight: (1 + Math.cos(Math.PI * offset)) / 2 };
});

/**
 * Smooths short route movements while allowing the rider to move around the aim.
 * The sampler must stay in the current route segment and unwrap longitudes.
 * Symmetric sampling avoids sustained lag on straight sections. No previous
 * frame is needed, so export workers and out-of-order seeks agree.
 */
export function smoothCameraTarget(
  sample: (fraction: number) => CameraTarget,
  fraction: number,
  traversalSeconds: number,
  maximumOffset: number,
  halfWindowSeconds = HALF_WINDOW_SECONDS,
): CameraTarget {
  const current = sample(fraction);
  if (halfWindowSeconds <= 0 || maximumOffset <= 0) return current;
  const radius = halfWindowSeconds / Math.max(1, traversalSeconds);
  let dx = 0;
  let dy = 0;
  let weightTotal = 0;
  for (const { offset, weight } of CAMERA_FOLLOW_KERNEL) {
    const point = sample(Math.max(0, Math.min(1, fraction + offset * radius)));
    dx += (point.x - current.x) * weight;
    dy += (point.y - current.y) * weight;
    weightTotal += weight;
  }
  dx /= weightTotal;
  dy /= weightTotal;

  // A smooth bound avoids a hard transition when a large bend fills the frame.
  const scale = maximumOffset / Math.hypot(maximumOffset, dx, dy);
  return { x: current.x + dx * scale, y: current.y + dy * scale };
}

/** Keeps camera drift proportional to the available frame at every zoom. */
export function cameraFollowAllowance(width: number, height: number, zoom: number): number {
  const pixels = Math.min(96, Math.min(width, height) * 0.16);
  return pixels / (512 * 2 ** zoom);
}
